import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Table,
  Button,
  Tag,
  Space,
  Switch,
  Popconfirm,
  Spin,
  message,
  Alert,
} from 'antd';
import {
  PlusCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from '@ant-design/icons';
import {
  getTaskRoutingRules,
  createTaskRoutingRule,
  updateTaskRoutingRule,
  deleteTaskRoutingRule,
  reorderTaskRoutingRules,
  getAllTaskTemplates,
  getAllLabelsForRoutingRules,
} from '../../../actions/taskRoutingRules';
import { getDocumentCategories } from '../../../actions/userProfile';
import AddTaskRoutingRuleModal from '../../modal/AddTaskRoutingRuleModal';

class ManageTaskRoutingRules extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      editingRule: null,
      localRules: [],
    };
  }

  componentDidMount = async () => {
    const {
      getTaskRoutingRules: fetchRules,
      getDocumentCategories: fetchCategories,
      getAllLabelsForRoutingRules: fetchLabels,
      getAllTaskTemplates: fetchTemplates,
    } = this.props;
    await Promise.all([
      fetchRules(),
      fetchCategories(),
      fetchLabels(),
      fetchTemplates(),
    ]);
    const { rules } = this.props;
    this.setState({ localRules: rules });
  };

  componentDidUpdate(prevProps) {
    const { rules } = this.props;
    if (prevProps.rules !== rules) {
      this.setState({ localRules: rules });
    }
  }

  openModal = (rule = null) => {
    this.setState({ showModal: true, editingRule: rule });
  };

  closeModal = () => {
    this.setState({ showModal: false, editingRule: null });
  };

  handleSaveRule = async (ruleData) => {
    const { editingRule } = this.state;
    const {
      createTaskRoutingRule: createRule,
      updateTaskRoutingRule: updateRule,
      getTaskRoutingRules: fetchRules,
    } = this.props;

    const isUpdate = !!editingRule;
    let result;
    if (isUpdate) {
      result = await updateRule(editingRule.id, ruleData);
    } else {
      result = await createRule(ruleData);
    }

    if (result?.error) {
      message.error(result.response?.data?.error || 'Failed to save routing rule');
    } else {
      message.success(isUpdate ? 'Rule updated successfully' : 'Rule created successfully');
      this.closeModal();
      await fetchRules();
    }
  };

  handleDeleteRule = async (ruleId) => {
    const {
      deleteTaskRoutingRule: deleteRule,
      getTaskRoutingRules: fetchRules,
    } = this.props;

    const result = await deleteRule(ruleId);

    if (result?.error) {
      message.error(result.response?.data?.error || 'Failed to delete routing rule');
    } else {
      message.success('Rule deleted successfully');
      await fetchRules();
    }
  };

  handleToggleActive = async (rule, active) => {
    const { updateTaskRoutingRule: updateRule, getTaskRoutingRules: fetchRules } = this.props;

    const ruleData = {
      name: rule.name,
      action_type: rule.action_type,
      action_id: rule.action_id,
      active,
      task_routing_conditions_attributes: rule.conditions.map((c) => ({
        id: c.id,
        kind: c.kind,
        operator: c.operator,
        value: c.value,
      })),
    };

    const result = await updateRule(rule.id, ruleData);

    if (result?.error) {
      message.error(result.response?.data?.error || 'Failed to toggle rule status');
    } else {
      await fetchRules();
    }
  };

  moveRule = async (fromIndex, toIndex) => {
    const { localRules } = this.state;
    const { reorderTaskRoutingRules: reorder, getTaskRoutingRules: fetchRules } = this.props;

    if (toIndex < 0 || toIndex >= localRules.length) return;

    const newRules = [...localRules];
    const [moved] = newRules.splice(fromIndex, 1);
    newRules.splice(toIndex, 0, moved);

    this.setState({ localRules: newRules });

    const ruleIds = newRules.map((r) => r.id);
    const result = await reorder(ruleIds);

    if (result?.error) {
      message.error('Failed to reorder rules');
      await fetchRules();
    }
  };

  checkDefaultRuleWarning = () => {
    const { localRules } = this.state;
    const warnings = [];

    localRules.forEach((rule, index) => {
      const ruleHasNoLabels = !rule.labels || rule.labels.length === 0;
      if (ruleHasNoLabels) {
        const laterSpecificRules = localRules.slice(index + 1).filter(
          (r) => (r.labels && r.labels.length > 0) && r.document_category?.id === rule.document_category?.id
        );
        if (laterSpecificRules.length > 0) {
          warnings.push(
            `"${rule.name || 'Default rule'}" (priority ${index + 1}) is a default rule that will catch all "${rule.document_category?.emr_category}" documents before more specific label rules below it.`
          );
        }
      }
    });

    return warnings;
  };

  render() {
    const { showModal, editingRule, localRules } = this.state;
    const {
      isLoading,
      isAdmin,
      userInfoLoaded,
      categories,
      labels,
      taskTemplates,
    } = this.props;

    // Allow editing if user is admin OR if user info hasn't loaded yet
    // (backend will reject if not admin, this just improves UX during load)
    const canEdit = isAdmin || !userInfoLoaded;

    const warnings = this.checkDefaultRuleWarning();

    const columns = [
      {
        title: 'Priority',
        key: 'priority',
        width: 100,
        align: 'center',
        render: (_text, _record, index) => (
          <Space size={4}>
            <Button
              type="text"
              icon={<ArrowUpOutlined />}
              onClick={() => this.moveRule(index, index - 1)}
              disabled={index === 0 || !canEdit}
              size="small"
            />
            <Button
              type="text"
              icon={<ArrowDownOutlined />}
              onClick={() => this.moveRule(index, index + 1)}
              disabled={index === localRules.length - 1 || !canEdit}
              size="small"
            />
            <span style={{ width: 24, display: 'inline-block', textAlign: 'right' }}>
              {index + 1}
            </span>
          </Space>
        ),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (name) => name || '-',
      },
      {
        title: 'Document Category',
        dataIndex: 'document_category',
        key: 'document_category',
        render: (category) => category?.emr_category || '-',
      },
      {
        title: 'Label',
        key: 'label',
        render: (_, record) => {
          const ruleLabels = record.labels || (record.label ? [record.label] : []);
          if (ruleLabels.length === 0) {
            return <span style={{ color: '#888', fontStyle: 'italic' }}>(No label - default)</span>;
          }
          const connector = record.label_match_mode === 'all' ? 'and' : 'or';
          return (
            <Space size={4} wrap>
              {ruleLabels.map((ruleLabel, idx) => {
                const textColor = ruleLabel.is_dark_text ? 'black' : 'white';
                return (
                  <React.Fragment key={ruleLabel.id}>
                    <Tag color={ruleLabel.color} style={{ color: textColor, marginRight: 0 }}>
                      {ruleLabel.name}
                    </Tag>
                    {idx < ruleLabels.length - 1 && ruleLabels.length > 1 && (
                      <span style={{ color: '#888', fontSize: '12px', margin: '0 4px' }}>{connector}</span>
                    )}
                  </React.Fragment>
                );
              })}
            </Space>
          );
        },
      },
      {
        title: 'Task Template',
        dataIndex: 'action',
        key: 'action',
        render: (action) => action?.task_name || '-',
      },
      {
        title: 'Active',
        dataIndex: 'active',
        key: 'active',
        width: 80,
        align: 'center',
        render: (active, record) => (
          <Switch
            checked={active}
            onChange={(checked) => this.handleToggleActive(record, checked)}
            disabled={!canEdit}
            size="small"
          />
        ),
      },
      {
        title: '',
        key: 'actions',
        width: 150,
        align: 'right',
        render: (_, record) => (
          <Space>
            <Button
              size="small"
              onClick={() => this.openModal(record)}
              disabled={!canEdit}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete this rule?"
              onConfirm={() => this.handleDeleteRule(record.id)}
              disabled={!canEdit}
            >
              <Button size="small" danger disabled={!canEdit}>
                Delete
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    if (isLoading && localRules.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      );
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '10px 40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <h2 style={{ margin: 0, marginRight: 16 }}>Task Routing Rules</h2>
            {canEdit && (
              <Button
                icon={<PlusCircleOutlined />}
                type="primary"
                onClick={() => this.openModal()}
              >
                Add Rule
              </Button>
            )}
          </div>
        </div>

        <Alert
          type="info"
          showIcon
          message="How routing rules work"
          description={(
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
              <li>Rules automatically create EMA tasks when faxes are uploaded with a matching document category</li>
              <li>Rules are evaluated top to bottom - the first match wins</li>
              <li>Rules without a label (&quot;No label - default&quot;) act as catch-alls and should be placed last</li>
              <li>
                <strong>Example:</strong>
                {' '}
                &quot;Prior Auth&quot; + &quot;Dr. Smith&quot; label → creates task from &quot;PA - Smith Team&quot; template
              </li>
            </ul>
          )}
          style={{ marginBottom: 16 }}
        />

        {warnings.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message="Ordering Warning"
            description={(
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          size="small"
          dataSource={localRules}
          columns={columns}
          rowKey={(record) => record.id}
          bordered
          pagination={false}
          loading={isLoading}
        />

        {showModal && (
          <AddTaskRoutingRuleModal
            visible={showModal}
            onClose={this.closeModal}
            onSave={this.handleSaveRule}
            editingRule={editingRule}
            categories={categories}
            labels={labels}
            taskTemplates={taskTemplates}
            existingRules={localRules}
            isLoading={isLoading}
          />
        )}
      </div>
    );
  }
}

ManageTaskRoutingRules.defaultProps = {
  rules: [],
  categories: [],
  labels: [],
  taskTemplates: [],
  isLoading: false,
  error: false,
  errorMessage: '',
  userInfoLoaded: false,
};

ManageTaskRoutingRules.propTypes = {
  rules: PropTypes.array,
  categories: PropTypes.array,
  labels: PropTypes.array,
  taskTemplates: PropTypes.array,
  isLoading: PropTypes.bool,
  isAdmin: PropTypes.bool.isRequired,
  userInfoLoaded: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  getTaskRoutingRules: PropTypes.func.isRequired,
  createTaskRoutingRule: PropTypes.func.isRequired,
  updateTaskRoutingRule: PropTypes.func.isRequired,
  deleteTaskRoutingRule: PropTypes.func.isRequired,
  reorderTaskRoutingRules: PropTypes.func.isRequired,
  getDocumentCategories: PropTypes.func.isRequired,
  getAllLabelsForRoutingRules: PropTypes.func.isRequired,
  getAllTaskTemplates: PropTypes.func.isRequired,
};

export default connect(
  (state) => ({
    rules: state.taskRoutingRules.rules,
    isLoading: state.taskRoutingRules.isLoading,
    error: state.taskRoutingRules.error,
    errorMessage: state.taskRoutingRules.errorMessage,
    isAdmin: state.userProfile.isAdmin,
    userInfoLoaded: !!state.userProfile.email,
    categories: state.userProfile.allDocumentCategories?.categories || [],
    labels: state.taskRoutingRules.labels || [],
    taskTemplates: state.taskRoutingRules.taskTemplates || [],
  }),
  {
    getTaskRoutingRules,
    createTaskRoutingRule,
    updateTaskRoutingRule,
    deleteTaskRoutingRule,
    reorderTaskRoutingRules,
    getDocumentCategories,
    getAllLabelsForRoutingRules,
    getAllTaskTemplates,
  }
)(ManageTaskRoutingRules);
